import { TFilterColumnOption, TFilterDateRangeValue, TFilterValue } from "@irohana-dev/components";
import { Dayjs } from "dayjs";

export type TFilterDisplayColumn = {
  id: string;
  label: string;
  visible: boolean;
  order: number;
};

export const asStringArray = (value: unknown): string[] => (Array.isArray(value) ? value.map(String) : []);

export const buildColumnOptions = (columns: TFilterDisplayColumn[]): TFilterColumnOption[] =>
  columns.map((col) => ({ key: col.id, label: col.label }));

export const toDateRangeValue = (from: Dayjs | null, to: Dayjs | null): TFilterDateRangeValue | undefined =>
  from || to ? { from, to } : undefined;

export const fromDateRangeValue = (value: TFilterValue, key: string): { from: Dayjs | null; to: Dayjs | null } => {
  const range = (value[key] ?? {}) as TFilterDateRangeValue;
  return { from: (range.from as Dayjs) ?? null, to: (range.to as Dayjs) ?? null };
};

export const applyColumnSettings = <TColumn extends TFilterDisplayColumn>(
  columns: TColumn[],
  order: string[],
  hidden: string[],
  defaults: TColumn[],
): TColumn[] => {
  const byId = new Map(columns.map((col) => [col.id, col]));
  const ordered = order.filter((id) => byId.has(id));
  const rest = columns.map((col) => col.id).filter((id) => !ordered.includes(id));
  return [...ordered, ...rest].map((id, index) => {
    const col = byId.get(id) ?? defaults.find((c) => c.id === id)!;
    return { ...col, visible: !hidden.includes(id), order: index + 1 };
  });
};

export const resolveSavedConditionColumns = <TColumn extends TFilterDisplayColumn>(
  condition: { includeColumnSettings?: boolean; columnSettings?: unknown },
  defaults: TColumn[],
): TColumn[] => {
  const settings = condition.columnSettings as { order?: string[]; hidden?: string[] } | null | undefined;
  if (!condition.includeColumnSettings || !settings) return [...defaults];
  return applyColumnSettings([...defaults], settings.order ?? [], settings.hidden ?? [], defaults);
};

export const hasDisplayColumnsChanged = <TColumn extends TFilterDisplayColumn>(columns: TColumn[] | null | undefined, defaults: TColumn[]) => {
  if (!columns) return false;
  const signature = (cols: TColumn[]) =>
    [...cols]
      .sort((a, b) => a.order - b.order)
      .map((col) => `${col.id}:${col.visible}`)
      .join(",");
  return signature(columns) !== signature(defaults);
};

export const enforceLockedColumns = <TColumn extends TFilterDisplayColumn>(columns: TColumn[], lockedIds: string[]): TColumn[] =>
  columns.map((col) => (lockedIds.includes(col.id) && !col.visible ? { ...col, visible: true } : col));
